import * as THREE from 'three';
import { isSceneLoaded } from '../sceneIO';
import { lookupOperator } from '../../operators/nonImmutable';

let doLog = false;
function log(...args) {
  if (doLog) console.log(...args);
}

export function updateReferenceList({ from, to }, nodeId, plugName, opIdx, key, refs) {
  const refKey = JSON.stringify([nodeId, plugName, opIdx, key]);
  const existingRefs = to[refKey];
  if (existingRefs) {
    for (let existingRefId of existingRefs) {
      if (refs.indexOf(existingRefId) === -1) {
        delete from[existingRefId][refKey];
        if (!Object.keys(from[existingRefId]).length) {
          delete from[existingRefId];
        }
      }
    }
  }

  for (let refId of refs) {
    if (!from[refId]) from[refId] = {};
    from[refId][refKey] = true;
  }

  to[refKey] = refs;
};

//
// refKey = JSON.stringify([nodeId,plugName,opIdx]);
// from: { [fileId]:  refKey },
// from: { [refId]:  { [refKey}: refPlug } }
// to:  { [refKey]: fileId },
//
export function updateReference(sceneGraph, { from, to }, nodeId, plugName, opIdx, key, op, refPlug) {
  let refId = op[key];
  if (refId && sceneGraph && !sceneGraph.nodes[refId]) {
    console.warn('No reference to:', refId, 'from', nodeId, plugName, opIdx, key);
    op[key] = refId = null;
  }

  const refKey = JSON.stringify([nodeId, plugName, opIdx, key]);
  const existingRef = to[refKey];
  if (existingRef) {
    if (existingRef !== refId) {
      delete from[existingRef][refKey];
      if (refId) {
        if (!from[refId]) from[refId] = {};
        from[refId][refKey] = refPlug || true;
        to[refKey] = refId;
      } else {
        if (!Object.keys(from[existingRef]).length) {
          delete from[existingRef];
        }

        delete to[refKey];
      }
    }
  } else if (refId) {
    if (!from[refId]) from[refId] = {};
    from[refId][refKey] = refPlug || true;
    to[refKey] = refId;
  }
};

export function evaluateSceneGraph(store, sceneId, sceneGraph) {
  let start = new Date();
  let i = 0;
  let id = null;

  const sceneNodeIds = Object.keys(sceneGraph.nodes);
  const evaluatedNodeIds = Object.keys(sceneGraph.evaluatedNodes);
  const { from, to } = sceneGraph.nodeReferences;

  // First evaluation after sceneGraph is loaded
  const transitionToLoaded = !sceneGraph.loaded && isSceneLoaded(store);
  if (!sceneGraph.loaded) sceneGraph.loaded = transitionToLoaded;

  const worldTransforms = {};
  const toUpdate = {};
  const toDelete = {};
  const updating = {};
  const nodeUpdates = {};

  function getEvaluated(id, plug) {
    if (updating[id] && !nodeUpdates[id]) {
      console.error('Circular reference, updating', sceneGraph.nodes[id].name);
      throw new Error('Circular reference: ' + id);
    }

    if (toUpdate[id] && !updating[id]) updateNode(id, toUpdate[id]);

    //addReference(id, {id: nodeId, sourcePlug, destPlug}, toPlugKey(destPlug, opIndex, fromKey));
    let result = nodeUpdates[id] || sceneGraph.evaluatedNodes[id];
    if (plug) result = result && result[plug];
    return result;
  }

  function getWorldTransform(id) {
    if (worldTransforms[id]) return worldTransforms[id];

    if (toUpdate[id] && !updating[id]) updateNode(id, toUpdate[id]);
    const localTransform = getEvaluated(id, 'Transform');
    if (!localTransform) return null;

    const node = sceneGraph.nodes[id];
    const parent = node && node.parent;
    const parentTransform = parent && getWorldTransform(parent);

    worldTransforms[id] = parentTransform
      ? new THREE.Matrix4().copy(parentTransform).multiply(localTransform.transform)
      : new THREE.Matrix4().copy(localTransform.transform);
    return worldTransforms[id];
  }

  function updateNode(id, updatePlug) {
    const node = sceneGraph.nodes[id];
    if(!node) return;

    let evalNode = sceneGraph.evaluatedNodes[id];
    if (!evalNode) {
      evalNode = sceneGraph.evaluatedNodes[id] = { name: node.name, _ev: 0, plugs: [] };
      if (node.plugs.Properties) evalNode.plugs.push('Properties');
      if (node.plugs.Transform) evalNode.plugs.push('Transform');
      for (let plug of Object.keys(node.plugs)) {
        if (plug !== 'Properties' && plug !== 'Transform') evalNode.plugs.push(plug);
      }
    }

    nodeUpdates[id] = evalNode;
    //console.log('evaluateSceneGraph::updateNode', node.name, updatePlug);
    updating[id] = true;

    let nodeEvaluatedVersion = 0;

    function doUpdatePlug(plug) {
      //console.log('  - ', plug);
      const ops = node.plugs[plug];
      const previousResult = evalNode[plug];
      const previousVersion = previousResult ? previousResult._v  : 0;

      evalNode[plug] = ops.reduce((result, op, idx) => {

        let operator = lookupOperator(plug, op.type, []);
        for (let key of operator.fileReferenceKeys) {
          updateReference(null, sceneGraph.fileReferences, id, plug, idx, key, op);
        }

        for (let key of operator.nodeReferenceKeys) {
          updateReference(sceneGraph, sceneGraph.nodeReferences, id, plug, idx, key, op);
        }

        let plugs = {};
        for (let key of Object.keys(operator.plugReferences)) {
          updateReference(sceneGraph, sceneGraph.nodeReferences, id, plug, idx, key, op, operator.plugReferences[key]);
          if (op[key]) plugs[key] = getEvaluated(op[key], operator.plugReferences[key]);
        }

        for (let key of operator.nodeReferenceListKeys) {
          updateReferenceList(sceneGraph.nodeReferences, id, plug, idx, key, op[key]);
        }

        if (operator.environment) {
          updateReference(sceneGraph, sceneGraph.nodeReferences, id, plug, idx, 'environment', { environment: sceneId }, 'Environment');
          plugs.environment = getEvaluated(sceneId, 'Environment');
        }

        return operator.update(Object.assign({}, op, plugs), result, {
          sceneId,
          node,
          store,
          properties: evalNode.Properties,
          getWorldTransform,
          previousResult,
          loaded: sceneGraph.loaded,
        });
      }, {});

      evalNode[plug]._v = previousVersion + 1;
    }

    for (let plug of evalNode.plugs) {
      if (updatePlug === true || updatePlug === plug) doUpdatePlug(plug);
      nodeEvaluatedVersion += evalNode[plug]._v;
    }

    evalNode._v = node._v;
    evalNode._ev = nodeEvaluatedVersion;

    delete updating[id];
    delete toUpdate[id];
  };

  for (let id of sceneNodeIds) {
    if (!sceneGraph.evaluatedNodes[id] || sceneGraph.evaluatedNodes[id]._v !== sceneGraph.nodes[id]._v) {
      toUpdate[id] = true;
    }
  }

  for (let id of evaluatedNodeIds) {
    if (!sceneGraph.nodes[id]) {
      toDelete[id] = true;
    }
  }

  if (transitionToLoaded) {
    for (let id of sceneNodeIds) {
      let evalNode = sceneGraph.evaluatedNodes[id];
      if (evalNode && evalNode.Properties && evalNode.Properties.prefetch) {
        toUpdate[id] = sceneGraph.nodes[id].type;
      }
    }
  }

  function needsUpdateReference(refId, fromPlug) {
    if (from[refId]) {
      for (let refKey of Object.keys(from[refId])) {
        const refPlug = from[refId][refKey];
        const [nodeId, plugName, opIdx, key] = JSON.parse(refKey);
        //console.log(' - ref ', sceneGraph.nodes[nodeId].name, '-', plugName, '-', key, 'fromPlug: ', toUpdate[nodeId]);

        if (!fromPlug || refPlug === true || refPlug === fromPlug) {
          if (!toUpdate[nodeId]) {
            toUpdate[nodeId] = plugName;
            needsUpdateReference(nodeId, plugName);
          } else if (toUpdate[nodeId] !== plugName) {
            toUpdate[nodeId] = true;
            needsUpdateReference(nodeId, plugName);
          }
        }
      }
    }
  }

  for (let id of Object.keys(toUpdate)) {
    if (from[id]) needsUpdateReference(id);
  }

  for (let id of Object.keys(toDelete)) {
    delete sceneGraph.evaluatedNodes[id];
  }

  // console.log('evalSceneGraph updating: ', Object.keys(toUpdate).length);
  for (let id of Object.keys(toUpdate)) {
    if (toUpdate[id]) updateNode(id, toUpdate[id]);
  }

  //if (nowLoaded) evalSceneGraph.loaded = true;

  // Store the current files and sceneGraph used to calculate evalSceneGraph
  //console.log('Update scene graph in: ', new Date() - start);

  sceneGraph._ev = sceneGraph._v;
}



// WEBPACK FOOTER //
// ./src/modules/utils/evalSceneGraph.js